import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  rotation: number;
  velocity: { x: number; y: number };
  rotationVelocity: number;
  shape: 'circle' | 'square' | 'triangle';
}

interface EnhancedConfettiProps {
  duration?: number;
  particleCount?: number;
  colors?: string[];
  zIndex?: number; // Ny prop för att kontrollera z-index
}

export const EnhancedConfetti: React.FC<EnhancedConfettiProps> = ({
  duration = 3000,
  particleCount = 100,
  colors,
  zIndex = 1000 // Defaultvärde för z-index
}) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    // Generera konfetti-partiklar
    const defaultColors = [
      '#FF9800', // orange primary
      '#FFEB3B', // gul
      '#4CAF50', // grön
      '#2196F3', // blå
      '#9C27B0', // lila
      '#F44336', // röd
      '#FFA726', // ljus orange
      '#66BB6A', // ljus grön
    ];

    const particleColors = colors || defaultColors;
    const shapes: Array<'circle' | 'square' | 'triangle'> = ['circle', 'square', 'triangle'];

    // Skapa partiklar med slumpmässiga egenskaper
    const newParticles: Particle[] = Array.from({ length: particleCount }, (_, i) => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      
      // Placera partiklar i övre delen av skärmen för en fallande effekt
      const x = Math.random() * screenWidth;
      const y = Math.random() * (screenHeight * 0.3) - screenHeight * 0.1; // Vissa börjar ovanför viewport
      
      // Generera slumpmässiga värden för mer naturlig rörelse
      const horizontalVelocity = (Math.random() - 0.5) * 8; // -4 till 4
      const verticalVelocity = Math.random() * 5 + 3; // 3 till 8
      const rotationVel = (Math.random() - 0.5) * 10; // -5 till 5
      const particleSize = Math.random() * 10 + 5; // 5-15px
      
      return {
        id: i,
        x,
        y,
        size: particleSize,
        color: particleColors[Math.floor(Math.random() * particleColors.length)],
        rotation: Math.random() * 360,
        velocity: {
          x: horizontalVelocity,
          y: verticalVelocity
        },
        rotationVelocity: rotationVel,
        shape: shapes[Math.floor(Math.random() * shapes.length)]
      };
    });

    setParticles(newParticles);

    // Sätt en timer för att stoppa animationen
    const timer = setTimeout(() => {
      setIsActive(false);
    }, duration);

    return () => clearTimeout(timer);
  }, [particleCount, duration, colors]);

  if (!isActive) return null;

  // Rendera olika former baserat på particle shape property
  const renderParticle = (particle: Particle) => {
    switch (particle.shape) {
      case 'circle':
        return (
          <motion.div
            key={particle.id}
            initial={{
              x: particle.x,
              y: particle.y,
              rotate: particle.rotation,
              scale: 0
            }}
            animate={{
              x: particle.x + particle.velocity.x * 100,
              y: particle.y + particle.velocity.y * 100,
              rotate: particle.rotation + particle.rotationVelocity * 50,
              scale: [0, 1, 1, 0.5, 0],
            }}
            transition={{
              duration: 2.5,
              ease: "easeOut",
              times: [0, 0.1, 0.7, 0.9, 1]
            }}
            style={{
              position: 'absolute',
              width: particle.size,
              height: particle.size,
              borderRadius: '50%',
              backgroundColor: particle.color,
            }}
          />
        );
      case 'square':
        return (
          <motion.div
            key={particle.id}
            initial={{
              x: particle.x,
              y: particle.y,
              rotate: particle.rotation,
              scale: 0
            }}
            animate={{
              x: particle.x + particle.velocity.x * 100,
              y: particle.y + particle.velocity.y * 100,
              rotate: particle.rotation + particle.rotationVelocity * 50,
              scale: [0, 1, 1, 0.5, 0],
            }}
            transition={{
              duration: 2.5,
              ease: "easeOut",
              times: [0, 0.1, 0.7, 0.9, 1]
            }}
            style={{
              position: 'absolute',
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
            }}
          />
        );
      case 'triangle':
        return (
          <motion.div
            key={particle.id}
            initial={{
              x: particle.x,
              y: particle.y,
              rotate: particle.rotation,
              scale: 0
            }}
            animate={{
              x: particle.x + particle.velocity.x * 100,
              y: particle.y + particle.velocity.y * 100,
              rotate: particle.rotation + particle.rotationVelocity * 50,
              scale: [0, 1, 1, 0.5, 0],
            }}
            transition={{
              duration: 2.5,
              ease: "easeOut",
              times: [0, 0.1, 0.7, 0.9, 1]
            }}
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              borderLeft: `${particle.size/2}px solid transparent`,
              borderRight: `${particle.size/2}px solid transparent`,
              borderBottom: `${particle.size}px solid ${particle.color}`,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: zIndex }} // Använd den angivna z-index
    >
      {particles.map(particle => renderParticle(particle))}
    </div>
  );
};

export default EnhancedConfetti;